import { ReactElement, useContext, useState } from 'react';

import { Field } from 'formik';
import gridStyle from '@pages/forms/Grid.module.scss';
import { Alert, Modal, TextInput } from '@components';
import VicFinesContext from '@pages/forms/VicFinesFormContext';
import InfoIcon from '../../../../images/info.svg';
import QuestionIcon from '../../../../images/question.svg';
// import mailRef from '../../../../images/refNo_mail.jpg';
import styles from './NominationStep1.module.scss';

const isIneligible = 'Ineligible'; // Error Message 1
const isNotInSystem = 'NotInSystem'; // Error Message 2

function NominationStep1(): ReactElement {
  const { apiError, tooltips } = useContext(VicFinesContext);
  const validTitle = Object.keys(tooltips).filter((v) => v.includes('obligationlocationonformstep1modal'));
  const modalContent = tooltips[validTitle[0]];
  const titleText = validTitle[0].substring(validTitle[0].indexOf('|'), validTitle[0].length).replaceAll('|', ' ');

  // Error Code 1
  const IneligibleForNomination = (): ReactElement => (
    <>
      <strong>You are not eligible to nominate for this offence. Once your fine has progressed to enforcement stage, it is too late to nominate.</strong>
      <br /><br />
      <p><a href="/Your-options/Pay-your-fine" target="_blank">Pay your fine</a></p>
      <br />
      <p>
        If you have only just found out about the fine, you may be eligible for an
        {' '}
        <a href="/infringement-extension" title="Infringement extension link">
          infringement extension
        </a>
        . This will give you more time to deal with the fine.
      </p>
      <br />
      <p>
        If you need assistance, call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          03 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        (regional callers). Monday to Friday from 8am to 6pm (except public holidays).
      </p>
    </>
  );

  const NotInSystemNomination = (): ReactElement => (
    <>
      <strong>The number you entered is not correct.</strong>
      <br />
      <br />
      <p>Please enter the obligation number or infringement notice number. This is usually located at the top of your infringement notice.</p>
    </>
  );

  // For Any other error
  const NominationStep1AlertContent = (): ReactElement => <p>{`${apiError?.Error}`}</p>;

  const [isOpenHelp, setOpenHelp] = useState(false);

  const closeNotifications = () => {
    setOpenHelp(false);
  };

  const NominationStep1NoteContent = (): ReactElement => (
    // TODO - Connect to Sitecore to make this content manageable.
    <>
      <p>You will need to enter the fine's obligation number or infringement number, the offence date and some other details about yourself or your vehicle.</p>
      <br />
      <span>
        If you don't have these details please <a href="/Contact-Us" target="_blank" title="Fines Victoria contact us page">contact us</a>.
      </span>
    </>
  );

  const notificationInformation = (): ReactElement => (
    // eslint-disable-next-line react/no-danger
    <div className={styles.accordContent} dangerouslySetInnerHTML={{ __html: modalContent }} />
  );

  return (
    <>
      <div className={gridStyle.form}>
        <div className="vfH2">Fine details</div>
        <span className="subHeadingH3">
          <p>Use this form to nominate the person who was driving.</p>
          <p>You can also let us know if your car was sold or stolen, or if you believe your number plates were stolen (police report recommended).</p>
          <p>If your fine is at Infringement stage, you can also use this form to advise us if you believe your number plates have been copied/cloned (police report recommended).</p>
          <p><b>You should nominate the driver as quickly as possible.</b> You can only apply to nominate a driver when the fine is at Infringement Notice or Penalty Reminder Notice stage.</p>
          <p>For some fines, you need to nominate within 28 days to avoid your licence being suspended.</p>
          <p>If you did not know about the fine and it is now too late to nominate, you may be able to <a href="/infringement-extension" title="Link to Infringement extension form">apply for an infringement extension</a>.</p>
        </span>
        <Field
          name="OBLIGATION_NUMBER"
          component={TextInput}
          title="Obligation number or infringement number"
          icon={QuestionIcon}
          iconPosition="end"
          iconClick={() => setOpenHelp(true)}
          isMandatory
          autocomplete="off"
        />
        {apiError?.Error && (
          <div className={styles.reduceGap}>
            {(apiError.Error === isIneligible) && (
              <Alert
                icon={InfoIcon}
                message={<IneligibleForNomination />}
                theme="error"
                classAlt={styles.boxheight}
              />
            )}
            {(apiError.Error === isNotInSystem) && (
              <Alert
                icon={InfoIcon}
                message={<NotInSystemNomination />}
                theme="error"
                classAlt={styles.boxheight}
              />
            )}
            {(apiError.Error !== isIneligible && apiError.Error !== isNotInSystem) && (
              <Alert
                icon={InfoIcon}
                message={<NominationStep1AlertContent />}
                theme="error"
                classAlt={styles.boxheight}
              />
            )}
          </div>
        )}
        {isOpenHelp && <Modal show close={closeNotifications} title={titleText} content={notificationInformation()} />}
      </div>
      <Alert message={<NominationStep1NoteContent />} theme="info" icon={InfoIcon} />
    </>
  );
}

export default NominationStep1;
